"use client";

import React, { useEffect, useRef } from "react";
import { AlgoliaConsentProvider, clearAlgoliaCookie, DEFAULT_ALGOLIA_CONSENT_CATEGORY, purgeAlgoliaLocalStorage, useResolvedAlgoliaConsent } from "@/lib/algoliaConsent";
interface AlgoliaInsightsBootstrapProps {
  appId?: string;
  apiKey?: string;
  oneTrustId?: string;
  consentCategoryId?: string;
  bypassConsentCheck?: boolean;
  isDevelopment?: boolean;
  cookieDurationMs?: number;
  children?: React.ReactNode;
}
const DEFAULT_COOKIE_DURATION_MS = 60 * 60 * 1000; // one hour

/**
 * Drives Algolia Insights (`aa`) from OneTrust consent.
 *
 * Behavior:
 *  - Mounts the `AlgoliaConsentProvider` so any descendant search
 *    component can subscribe via `useAlgoliaConsent()`.
 *  - Defers all interaction with `search-insights` until the
 *    configured OneTrust category (default C0002 Performance) is
 *    accepted. We DO NOT call `aa("init")` at module-load time — the
 *    `_ALGOLIA` cookie is only set after consent is granted.
 *  - On revoke (user toggles the OneTrust preference center or
 *    declines on the banner): re-initializes `aa` with
 *    `useCookie: false`, clears the `_ALGOLIA` cookie across the
 *    current host and every parent domain, and purges
 *    `nav-search-recents` / `searches` from localStorage.
 *
 * When OneTrust is not configured for the profile (dev / preview /
 * draft mode) or `bypassConsentCheck` is set, consent is granted
 * immediately to keep search debugging usable.
 */
export default function AlgoliaInsightsBootstrap({
  appId,
  apiKey,
  oneTrustId,
  consentCategoryId = DEFAULT_ALGOLIA_CONSENT_CATEGORY,
  bypassConsentCheck,
  isDevelopment,
  cookieDurationMs = DEFAULT_COOKIE_DURATION_MS,
  children
}: AlgoliaInsightsBootstrapProps) {
  const hasConsent = useResolvedAlgoliaConsent({
    oneTrustId,
    consentCategoryId,
    bypassConsentCheck,
    isDevelopment
  });

  // Dedupes the cookie-cleanup on a revoke from running its work on the
  // very first render when consent has never been granted (nothing to
  // clear). We still WANT to clear if the user accepts then declines in
  // the same session, so we track whether we ever called aa("init",
  // useCookie:true) and only run teardown on transitions out of that.
  const wasGrantedRef = useRef(false);
  useEffect(() => {
    if (!appId || !apiKey) return;
    let cancelled = false;
    if (hasConsent) {
      // Lazy import keeps search-insights out of the bundle for pages
      // that never need search until consent is granted.
      import("search-insights").then(({
        default: aa
      }) => {
        if (cancelled) return;
        aa("init", {
          appId,
          apiKey,
          useCookie: true,
          cookieDuration: cookieDurationMs
        });
        wasGrantedRef.current = true;
      }).catch(err => {
        console.warn("[AlgoliaInsightsBootstrap] failed to load", err);
      });
      return () => {
        cancelled = true;
      };
    }

    // hasConsent is false. Only do teardown if we previously granted —
    // otherwise nothing has touched the cookie yet.
    if (!wasGrantedRef.current) {
      // Still purge any stale storage left over from a prior session
      // (e.g. user accepted on a previous visit then declined on this
      // one, before any aa() call in this session).
      clearAlgoliaCookie();
      purgeAlgoliaLocalStorage();
      return;
    }
    import("search-insights").then(({
      default: aa_0
    }) => {
      if (cancelled) return;
      // Flipping useCookie:false detaches future writes; clearing the
      // existing cookie removes the historical token. Calling
      // aa("init", ...) again is safe — search-insights documents
      // re-init as supported.
      aa_0("init", {
        appId,
        apiKey,
        useCookie: false
      });
    }).catch(() => {
      // If the module fails to load on revoke we still want to wipe
      // the cookie + storage below.
    }).finally(() => {
      if (cancelled) return;
      clearAlgoliaCookie();
      purgeAlgoliaLocalStorage();
      wasGrantedRef.current = false;
    });
    return () => {
      cancelled = true;
    };
  }, [appId, apiKey, hasConsent, cookieDurationMs]);
  return <AlgoliaConsentProvider value={hasConsent}>
      {children}
    </AlgoliaConsentProvider>;
}